import { Search } from 'lucide-react';
import { useState } from 'react';

import { useTheme } from '../../hooks/useTheme';

import { NAV_THEMES } from './nav-tokens';

interface SearchTriggerProps {
  /** `pill` is the top bar's search field; `icon` is the mobile bar's button. */
  variant: 'pill' | 'icon';
  onOpen: () => void;
}

const PLACEHOLDER = 'Search venues, bookings, private events…';
const TITLE = 'Search (⌘K)';

/** Opens the command palette. The palette itself is owned by NebariNav, so
 *  every trigger drives the same one. */
export default function SearchTrigger({ variant, onOpen }: SearchTriggerProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const [hover, setHover] = useState(false);

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={TITLE}
        title={TITLE}
        className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-0"
        style={{ background: t.surface, color: t.ink }}
      >
        <Search size={20} aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={TITLE}
      title={TITLE}
      className="inline-flex h-[34px] w-full max-w-[440px] cursor-pointer items-center gap-2.5 rounded-lg px-3 text-[13.5px] transition-colors"
      style={{
        color: hover ? t.ink : t.muted,
        background: hover ? t.bg : t.surface,
        border: `1px solid ${hover ? t.subtle : t.border}`,
      }}
    >
      <Search size={16} aria-hidden className="shrink-0" />
      <span className="flex-1 truncate text-left">{PLACEHOLDER}</span>
      <kbd
        className="shrink-0 rounded px-1.5 py-0.5 text-[11px]"
        style={{
          fontFamily: 'var(--font-mono, ui-monospace)',
          background: t.bg,
          border: `1px solid ${t.border}`,
          color: t.subtle,
        }}
      >
        ⌘K
      </kbd>
    </button>
  );
}
